import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { SupplierNetwork } from '../../domain/entities';

interface ModalState {
    isOpen: boolean;
    message: string;
}

interface ConfirmModalState extends ModalState {
    onConfirm: (() => void) | null;
}

interface AppContextData {
    supplierNetwork: SupplierNetwork | null;
    setSupplierNetwork: (network: SupplierNetwork) => void;
    
    errorModal: ModalState;
    showError: (message: string) => void;
    closeError: () => void;
    
    successModal: ModalState;
    showSuccess: (message: string) => void;
    closeSuccess: () => void;
    
    confirmModal: ConfirmModalState;
    showConfirm: (message: string, onConfirm: () => void) => void;
    closeConfirm: () => void;
}

const AppContext = createContext<AppContextData | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [supplierNetwork, setSupplierNetwork] = useState<SupplierNetwork | null>(null);
    
    const [errorModal, setErrorModal] = useState<ModalState>({
        isOpen: false,
        message: '',
    });
    
    const [successModal, setSuccessModal] = useState<ModalState>({
        isOpen: false,
        message: '',
    });
    
    const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
        isOpen: false,
        message: '',
        onConfirm: null,
    });

    const showError = useCallback((message: string) => {
        setErrorModal({ isOpen: true, message });
    }, []);

    const closeError = useCallback(() => {
        setErrorModal({ isOpen: false, message: '' });
    }, []);

    const showSuccess = useCallback((message: string) => {
        setSuccessModal({ isOpen: true, message });
    }, []);

    const closeSuccess = useCallback(() => {
        setSuccessModal({ isOpen: false, message: '' });
    }, []);

    const showConfirm = useCallback((message: string, onConfirm: () => void) => {
        setConfirmModal({ isOpen: true, message, onConfirm });
    }, []);

    const closeConfirm = useCallback(() => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null });
    }, []);

    const handleConfirm = useCallback(() => {
        if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
        }
        closeConfirm();
    }, [confirmModal, closeConfirm]);

    return (
        <AppContext.Provider
            value={{
                supplierNetwork,
                setSupplierNetwork,
                errorModal,
                showError,
                closeError,
                successModal,
                showSuccess,
                closeSuccess,
                confirmModal: { ...confirmModal, onConfirm: handleConfirm },
                showConfirm,
                closeConfirm,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export const useApp = (): AppContextData => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within an AppProvider');
    }
    return context;
};

